/**
 * useBrands Hooks (Story 40-2 — Epic 40 Brand Management UI)
 *
 * TanStack Query data layer over the /api/brands endpoints (Story 40-1):
 *   - useBrands()            GET    /api/brands           -> Brand[] (+ memberCount)
 *   - useBrand(id)           GET    /api/brands/:id       -> BrandDetail (+ clients[])
 *   - useBrandMutations()    create / update / delete / assignClients / unassignClient
 *
 * All responses are { data } wrapped (unwrapped here). Mutations invalidate
 * ["brands"], ["brands", id], and ["clients"] (the client list shows the brand
 * badge). Errors throw the parsed RFC 9457 problem object so callers can read
 * `.detail`/`.title`. The server gates every endpoint on MANAGE_CLIENTS — a
 * viewer's list query 403s (callers render a permission/empty state).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/apiClient";

// ============================================================================
// Types
// ============================================================================

export interface Brand {
  id: string;
  name: string;
  ownerContactName: string | null;
  ownerContactEmail: string | null;
  logoUrl: string | null;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BrandMemberSummary {
  id: string;
  name: string;
  logoUrl: string | null;
}

export interface BrandDetail extends Brand {
  clients: BrandMemberSummary[];
}

export interface BrandFormValues {
  name: string;
  ownerContactName?: string | null;
  ownerContactEmail?: string | null;
  logoUrl?: string | null;
}

/** Max clients assignable in one POST /:id/clients call (server cap, Story 40-1). */
export const BRAND_ASSIGN_BATCH_LIMIT = 90;

// ============================================================================
// Query keys
// ============================================================================

export const BRANDS_QUERY_KEY = ["brands"] as const;
export function brandKey(id: string | undefined) {
  return ["brands", id] as const;
}

// ============================================================================
// Fetch helpers
// ============================================================================

async function unwrap<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const problem = await res.json().catch(() => ({}));
    throw {
      status: res.status,
      ...(problem as Record<string, unknown>),
      message:
        (problem as { detail?: string; title?: string })?.detail ??
        (problem as { title?: string })?.title ??
        fallback,
    };
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

// ============================================================================
// Queries
// ============================================================================

export function useBrands() {
  return useQuery<Brand[]>({
    queryKey: BRANDS_QUERY_KEY,
    queryFn: () => apiGet("/api/brands").then((r) => unwrap<Brand[]>(r, "Failed to load brands")),
  });
}

export function useBrand(id: string | undefined) {
  return useQuery<BrandDetail>({
    queryKey: brandKey(id),
    queryFn: () =>
      apiGet(`/api/brands/${id}`).then((r) => unwrap<BrandDetail>(r, "Failed to load brand")),
    enabled: !!id,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export function useBrandMutations() {
  const queryClient = useQueryClient();

  const invalidateAll = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: BRANDS_QUERY_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: brandKey(id) });
    // The client list/detail render the brand badge.
    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  const createBrand = useMutation({
    mutationFn: (values: BrandFormValues) =>
      apiPost("/api/brands", values).then((r) => unwrap<Brand>(r, "Failed to create brand")),
    onSuccess: () => invalidateAll(),
  });

  const updateBrand = useMutation({
    mutationFn: ({ id, ...values }: BrandFormValues & { id: string }) =>
      apiPatch(`/api/brands/${id}`, values).then((r) =>
        unwrap<Brand>(r, "Failed to update brand")
      ),
    onSuccess: (_data, vars) => invalidateAll(vars.id),
  });

  const deleteBrand = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/brands/${id}`).then((r) =>
        unwrap<{ id: string; clientsReturnedToStandalone: number }>(r, "Failed to delete brand")
      ),
    onSuccess: (_data, id) => invalidateAll(id),
  });

  const assignClients = useMutation({
    mutationFn: async ({ brandId, clientIds }: { brandId: string; clientIds: string[] }) => {
      // Chunk to the server's 90-per-request cap (Story 40-1 MAX_ASSIGN_BATCH).
      for (let i = 0; i < clientIds.length; i += BRAND_ASSIGN_BATCH_LIMIT) {
        const chunk = clientIds.slice(i, i + BRAND_ASSIGN_BATCH_LIMIT);
        const res = await apiPost(`/api/brands/${brandId}/clients`, { clientIds: chunk });
        await unwrap<unknown>(res, "Failed to assign clients");
      }
    },
    onSuccess: (_data, vars) => invalidateAll(vars.brandId),
  });

  const unassignClient = useMutation({
    mutationFn: async ({ brandId, clientId }: { brandId: string; clientId: string }) => {
      const res = await apiDelete(`/api/brands/${brandId}/clients/${clientId}`);
      if (!res.ok && res.status !== 204) {
        await unwrap<unknown>(res, "Failed to unassign client");
      }
    },
    onSuccess: (_data, vars) => invalidateAll(vars.brandId),
  });

  return { createBrand, updateBrand, deleteBrand, assignClients, unassignClient };
}
